import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Swords, Shield, Target, ChevronDown, ChevronUp, Zap, Users, Star } from 'lucide-react';
import { getPokemonImage, createPokemonImageErrorHandler, useImageMapReady } from '../utils/pokemonImageResolver';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiBaseUrl } from '../utils/apiUrl';

const PokemonCounters = ({ pokemonId, pokemonName }) => {
  const { t, gamePokemonName } = useLanguage();
  const navigate = useNavigate();
  const imageMapReady = useImageMapReady();
  const [countersData, setCountersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Default tab: Raid for normal Pokemon, Max Battle for Gigantamax
  const [activeTab, setActiveTab] = useState(() => {
    // Check if Gigantamax on initial load
    const isGigantamax = pokemonId?.toLowerCase().includes('gigantamax') || pokemonName?.toLowerCase().includes('gigantamax');
    return isGigantamax ? 'max' : 'raid';
  });

  const fetchCounters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/api/counters/pokemon/${pokemonId}`);
      const data = await response.json();
      
      if (data.success) {
        setCountersData(data.data);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [pokemonId]);

  // Check if this is a Mega Pokemon
  const isMegaPokemon = pokemonId?.toLowerCase().includes('mega') || pokemonName?.toLowerCase().includes('mega');
  
  // Check if this is a Gigantamax Pokemon
  const isGigantamaxPokemon = pokemonId?.toLowerCase().includes('gigantamax') || pokemonName?.toLowerCase().includes('gigantamax');

  // Use raw data without filtering Mega Pokemon
  const maxCounters = countersData?.maxCounters || [];
  const maxDefenders = countersData?.maxDefenders || [];
  const raidCounters = countersData?.raidCounters || [];

  // Order tabs: Raid first, then Max Battle, then Defenders
  // Hide Max Battle and Defenders tabs for Mega Pokemon
  // Hide Raid tab for Gigantamax Pokemon
  const allTabs = [
    { id: 'raid', label: t('counters.raid'), icon: Target, color: 'from-purple-500 to-pink-500', data: raidCounters, hideForGigantamax: true },
    { id: 'max', label: t('counters.maxBattle'), icon: Swords, color: 'from-red-500 to-orange-500', data: maxCounters, hideForMega: true },
    { id: 'defenders', label: t('counters.defenders'), icon: Shield, color: 'from-blue-500 to-cyan-500', data: maxDefenders, hideForMega: true }
  ];
  
  // Filter tabs: 
  // - Remove Max Battle and Defenders for Mega Pokemon
  // - Remove Raid for Gigantamax Pokemon
  const tabs = allTabs
    .filter(tab => {
      // Hide Max Battle and Defenders for Mega Pokemon
      if (isMegaPokemon && tab.hideForMega) {
        return false;
      }
      // Hide Raid for Gigantamax Pokemon
      if (isGigantamaxPokemon && tab.hideForGigantamax) {
        return false;
      }
      // Only show tabs with data
      return tab.data && tab.data.length > 0;
    });

  // All useEffect hooks must be called before any early returns
  useEffect(() => {
    fetchCounters();
  }, [pokemonId, fetchCounters]);

  // If current active tab is hidden (e.g., Max Battle for Mega, Raid for Gigantamax), switch to first available tab
  useEffect(() => {
    if (tabs.length === 0) return;
    
    const availableTabIds = tabs.map(t => t.id);
    if (!availableTabIds.includes(activeTab)) {
      // For Gigantamax, default to Max Battle; for Mega, default to Raid; otherwise use first available
      const defaultTab = isGigantamaxPokemon ? 'max' : (isMegaPokemon ? 'raid' : (availableTabIds[0] || 'raid'));
      setActiveTab(defaultTab);
    }
  }, [tabs, activeTab, isGigantamaxPokemon, isMegaPokemon]);

  // Get move type color
  const getMoveTypeColor = (moveName) => {
    if (!moveName) return 'bg-gray-100 text-gray-700';
    const name = moveName.toLowerCase();
    
    if (name.includes('fire') || name.includes('flame') || name.includes('burn') || name.includes('blaze') || name.includes('ember') || name.includes('incinerate')) 
      return 'bg-gradient-to-r from-orange-500 to-red-500 text-white';
    if (name.includes('water') || name.includes('surf') || name.includes('hydro') || name.includes('aqua')) 
      return 'bg-gradient-to-r from-blue-400 to-blue-600 text-white';
    if (name.includes('grass') || name.includes('leaf') || name.includes('vine') || name.includes('solar') || name.includes('petal')) 
      return 'bg-gradient-to-r from-green-400 to-green-600 text-white';
    if (name.includes('electric') || name.includes('thunder') || name.includes('spark') || name.includes('volt')) 
      return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-800';
    if (name.includes('psychic') || name.includes('psystrike') || name.includes('confusion') || name.includes('mind')) 
      return 'bg-gradient-to-r from-pink-400 to-purple-500 text-white';
    if (name.includes('ice') || name.includes('freeze') || name.includes('frost') || name.includes('blizzard')) 
      return 'bg-gradient-to-r from-cyan-300 to-blue-400 text-white';
    if (name.includes('dragon') || name.includes('draco') || name.includes('outrage')) 
      return 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white';
    if (name.includes('dark') || name.includes('shadow') || name.includes('night') || name.includes('crunch')) 
      return 'bg-gradient-to-r from-gray-700 to-gray-900 text-white';
    if (name.includes('fairy') || name.includes('charm') || name.includes('dazzle') || name.includes('moonblast')) 
      return 'bg-gradient-to-r from-pink-300 to-pink-500 text-white';
    if (name.includes('fighting') || name.includes('punch') || name.includes('kick') || name.includes('counter')) 
      return 'bg-gradient-to-r from-red-500 to-orange-600 text-white';
    if (name.includes('poison') || name.includes('toxic') || name.includes('sludge') || name.includes('acid')) 
      return 'bg-gradient-to-r from-purple-400 to-purple-600 text-white';
    if (name.includes('ground') || name.includes('earth') || name.includes('mud') || name.includes('sand')) 
      return 'bg-gradient-to-r from-yellow-600 to-amber-700 text-white';
    if (name.includes('flying') || name.includes('wing') || name.includes('air') || name.includes('sky') || name.includes('aerial')) 
      return 'bg-gradient-to-r from-indigo-300 to-blue-400 text-white';
    if (name.includes('bug') || name.includes('fury') || name.includes('megahorn')) 
      return 'bg-gradient-to-r from-lime-400 to-green-500 text-white';
    if (name.includes('rock') || name.includes('stone') || name.includes('smack')) 
      return 'bg-gradient-to-r from-yellow-700 to-stone-600 text-white';
    if (name.includes('ghost') || name.includes('hex') || name.includes('lick')) 
      return 'bg-gradient-to-r from-purple-600 to-indigo-700 text-white';
    if (name.includes('steel') || name.includes('metal') || name.includes('iron') || name.includes('flash cannon') || name.includes('bullet')) 
      return 'bg-gradient-to-r from-gray-400 to-gray-600 text-white';
    
    return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
  };


  // Don't show if no data
  const hasMaxCounters = maxCounters && maxCounters.length > 0;
  const hasMaxDefenders = maxDefenders && maxDefenders.length > 0;
  const hasRaidCounters = raidCounters && raidCounters.length > 0;

  // Early returns after all hooks
  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-lg">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded-lg mb-6 w-2/3"></div>
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gray-200 rounded-xl"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !countersData || (!hasMaxCounters && !hasMaxDefenders && !hasRaidCounters)) {
    return null;
  }
  
  // If no tabs available after filtering, don't show component
  if (tabs.length === 0) {
    return null;
  }
  
  const activeTabData = tabs.find(t => t.id === activeTab) || tabs[0];

  // Counter Card Component
  const CounterCard = ({ counter, index, type }) => {
    const rank = type === 'defenders' ? counter.defender_rank : counter.counter_rank;
    const pokemonName = type === 'defenders' ? counter.defender_pokemon : counter.counter_pokemon;
    const fastMove = counter.fast_move;
    const chargedMove = type === 'defenders' ? null : counter.charged_move;
    // Removed currentImageIndex - using single source of truth resolver
    
    // Removed image index reset - using single source of truth resolver
    
    // Convert Pokemon name to ID format for image loading
    // Extract Pokemon ID from name (e.g., "Gigantamax Charizard" -> "6-Gigantamax", "Shadow Rhyperior" -> "464")
    const getPokemonIdFromName = (name) => {
      if (!name) return null;
      let nameLower = name.toLowerCase();
      
      // Remove Shadow prefix (Shadow Pokemon use base image)
      if (nameLower.startsWith('shadow ')) {
        nameLower = nameLower.replace('shadow ', '');
      }
      
      // Handle Crowned forms (Crowned Shield Zamazenta, Crowned Sword Zacian)
      // Return full ID for images and navigation: "888-Crowned_Sword" or "889-Crowned_Shield"
      if (nameLower.includes('crowned shield') && nameLower.includes('zamazenta')) {
        return '889-Crowned_Shield';
      } else if (nameLower.includes('crowned sword') && nameLower.includes('zacian')) {
        return '888-Crowned_Sword';
      } else if (nameLower.includes('crowned shield')) {
        // Try to determine from remaining name
        const remaining = nameLower.replace('crowned shield', '').trim();
        if (remaining.includes('zamazenta') || remaining === 'zamazenta') {
          return '889-Crowned_Shield';
        }
      } else if (nameLower.includes('crowned sword')) {
        // Try to determine from remaining name
        const remaining = nameLower.replace('crowned sword', '').trim();
        if (remaining.includes('zacian') || remaining === 'zacian') {
          return '888-Crowned_Sword';
        }
      } else if (nameLower.includes('crowned')) {
        // Generic crowned - try to determine from name
        if (nameLower.includes('zamazenta')) {
          return '889-Crowned_Shield';
        } else if (nameLower.includes('zacian')) {
          return '888-Crowned_Sword';
        }
      }
      
      // Check for variants
      if (nameLower.includes('gigantamax')) {
        const baseName = nameLower.replace('gigantamax ', '').trim();
        // Try to extract ID from common names
        const nameMap = {
          'charizard': '6', 'venusaur': '3', 'blastoise': '9',
          'butterfree': '12', 'pikachu': '25', 'meowth': '52',
          'machamp': '68', 'gengar': '94', 'kingler': '99',
          'lapras': '131', 'eevee': '133', 'snorlax': '143',
          'garbodor': '569', 'melmetal': '809', 'rillaboom': '812',
          'cinderace': '815', 'inteleon': '818', 'corviknight': '823',
          'orbeetle': '826', 'drednaw': '834', 'coalossal': '839',
          'flapple': '841', 'appletun': '842', 'sandaconda': '844',
          'toxtricity': '849', 'centiskorch': '851', 'hatterene': '858',
          'grimmsnarl': '861', 'alcremie': '869', 'copperajah': '879',
          'duraludon': '884'
        };
        const baseId = nameMap[baseName];
        return baseId ? `${baseId}-Gigantamax` : null;
      } else if (nameLower.includes('mega')) {
        const baseName = nameLower.replace(/mega\s*(x|y)?\s*/i, '').trim();
        const nameMap = {
          'charizard': '6', 'venusaur': '3', 'blastoise': '9',
          'pidgeot': '18', 'alakazam': '65', 'gengar': '94',
          'kangaskhan': '115', 'pinsir': '127', 'gyarados': '130',
          'aerodactyl': '142', 'mewtwo': '150', 'ampharos': '181',
          'scizor': '212', 'heracross': '214', 'houndoom': '229',
          'tyranitar': '248', 'blaziken': '257', 'gardevoir': '282',
          'mawile': '303', 'aggron': '306', 'medicham': '308',
          'manectric': '310', 'banette': '354', 'absol': '359',
          'garchomp': '445', 'lucario': '448', 'abomasnow': '460',
          'beedrill': '15', 'pidgeot': '18', 'slowbro': '80',
          'steelix': '208', 'sceptile': '254', 'swampert': '260',
          'sableye': '302', 'altaria': '334', 'camerupt': '323',
          'sharpedo': '319', 'glalie': '362', 'salamence': '373',
          'metagross': '376', 'rayquaza': '384', 'lopunny': '428',
          'gallade': '475', 'audino': '531', 'diancie': '719',
          'latias': '380', 'latios': '381'
        };
        const baseId = nameMap[baseName];
        if (!baseId) return null;
        if (nameLower.includes(' x')) return `${baseId}-Mega_X`;
        if (nameLower.includes(' y')) return `${baseId}-Mega_Y`;
        return `${baseId}-Mega`;
      }
      
      // Try to find base Pokemon ID
      const nameMap = {
        'bulbasaur': '1', 'ivysaur': '2', 'venusaur': '3',
        'charmander': '4', 'charmeleon': '5', 'charizard': '6',
        'squirtle': '7', 'wartortle': '8', 'blastoise': '9',
        'pikachu': '25', 'raichu': '26', 'gengar': '94',
        'machamp': '68', 'alakazam': '65', 'dragonite': '149',
        'tyranitar': '248', 'salamence': '373', 'metagross': '376',
        'garchomp': '445', 'lucario': '448', 'rayquaza': '384',
        'mewtwo': '150', 'mew': '151', 'snorlax': '143',
        'lapras': '131', 'eevee': '133', 'vaporeon': '134',
        'jolteon': '135', 'flareon': '136', 'espeon': '196',
        'umbreon': '197', 'leafeon': '470', 'glaceon': '471',
        'sylveon': '700',         'omastar': '139', 'kabutops': '141',
        'rhyperior': '464', 'rhydon': '112', 'rhyhorn': '111',
        'machop': '66', 'machoke': '67', 'machamp': '68',
        'abra': '63', 'kadabra': '64', 'alakazam': '65',
        'gastly': '92', 'haunter': '93', 'gengar': '94',
        'geodude': '74', 'graveler': '75', 'golem': '76',
        'bulbasaur': '1', 'ivysaur': '2', 'venusaur': '3',
        'charmander': '4', 'charmeleon': '5', 'charizard': '6',
        'squirtle': '7', 'wartortle': '8', 'blastoise': '9',
        'pikachu': '25', 'raichu': '26', 'eevee': '133',
        'vaporeon': '134', 'jolteon': '135', 'flareon': '136',
        'espeon': '196', 'umbreon': '197', 'leafeon': '470',
        'glaceon': '471', 'sylveon': '700', 'mewtwo': '150',
        'mew': '151', 'dragonite': '149', 'tyranitar': '248',
        'salamence': '373', 'metagross': '376', 'garchomp': '445',
        'lucario': '448', 'rayquaza': '384', 'groudon': '383',
        'kyogre': '382', 'dialga': '483', 'palkia': '484',
        'giratina': '487', 'reshiram': '643', 'zekrom': '644',
        'kyurem': '646', 'xerneas': '716', 'yveltal': '717',
        'zygarde': '718', 'solgaleo': '791', 'lunala': '792',
        'necrozma': '800', 'zacian': '888', 'zamazenta': '889',
        'eternatus': '890', 'calyrex': '898'
      };
      const baseId = nameMap[nameLower] || null;
      return baseId;
    };

    // Get image using Pokemon ID - SINGLE SOURCE OF TRUTH
    const pokemonIdForImage = getPokemonIdFromName(pokemonName);
    const currentImage = useMemo(() => {
      if (pokemonIdForImage) {
        return getPokemonImage(pokemonIdForImage, 'default');
      }
      // Fallback to placeholder if no ID found
      return '/images/pokemon-spirit/000.png';
    }, [pokemonIdForImage, imageMapReady]);
    
    const handleImageError = createPokemonImageErrorHandler(pokemonIdForImage || pokemonName, { variant: 'default' });
    
    // Get medal color for top 3
    const getMedalColor = (rank) => {
      if (rank === 1) return 'from-yellow-400 to-amber-500 shadow-yellow-300/50';
      if (rank === 2) return 'from-gray-300 to-gray-400 shadow-gray-300/50';
      if (rank === 3) return 'from-amber-600 to-orange-700 shadow-orange-300/50';
      return 'from-gray-200 to-gray-300';
    };

    // Get Pokemon ID for navigation
    // For Crowned forms, try to find the full ID (e.g., "888-Crowned_Sword" or "889-Crowned_Shield")
    const getPokemonIdForNavigation = () => {
      // Always use getPokemonIdFromName since CounterCard doesn't receive pokemonId prop
      // getPokemonIdFromName already handles Crowned forms, Mega, Gigantamax, etc.
      const id = getPokemonIdFromName(pokemonName);
      return id || null;
    };

    // Get navigation ID
    const navId = useMemo(() => {
      return getPokemonIdForNavigation();
    }, [pokemonName]);
    
    const handleCardClick = (e) => {
      // Don't navigate if clicking on specific interactive elements
      const target = e.target;
      const isClickableElement = target.closest('button') || 
                                 target.closest('a') || 
                                 target.tagName === 'BUTTON' || 
                                 target.tagName === 'A';
      
      if (isClickableElement) {
        return;
      }
      
      // Get navId fresh each time to ensure we have the latest value
      const currentNavId = getPokemonIdForNavigation();
      
      // If we have a navId, navigate
      if (currentNavId) {
        console.log('Navigating to:', `/pokemon/${currentNavId}`, 'from:', pokemonName);
        e.preventDefault();
        e.stopPropagation();
        navigate(`/pokemon/${currentNavId}`);
      } else {
        console.warn('No navigation ID found for:', pokemonName, 'pokemonId:', pokemonId);
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        onClick={handleCardClick}
        className={`
          relative overflow-hidden rounded-2xl cursor-pointer
          ${rank <= 3 ? 'bg-gradient-to-br from-white to-gray-50 shadow-xl border-2 border-white' : 'bg-white shadow-md border border-gray-100'}
          hover:shadow-xl hover:scale-[1.02] transition-all duration-300
        `}
      >
        {/* Rank Badge */}
        <div className={`
          absolute -top-1 -left-1 w-10 h-10 rounded-br-2xl flex items-center justify-center
          bg-gradient-to-br ${getMedalColor(rank)} shadow-lg
        `}>
          <span className={`font-bold ${rank <= 3 ? 'text-white text-lg' : 'text-gray-600 text-sm'}`}>
            {rank}
          </span>
        </div>

        <div className="p-4 pl-12">
          <div className="flex items-center space-x-4">
            {/* Pokemon Image with fallback support */}
            <div className="relative">
              <div 
                className={`
                  w-16 h-16 rounded-2xl overflow-hidden relative
                  flex items-center justify-center
                  ${rank <= 3 ? 'ring-2 ring-white shadow-lg' : ''}
                  ${pokemonName.toLowerCase().includes('shadow') 
                    ? '' 
                    : 'bg-gradient-to-br from-gray-100 to-gray-200 shadow-inner'
                  }
                `}
              >
                {/* Background for Shadow Pokemon */}
                {pokemonName.toLowerCase().includes('shadow') && (
                  <img
                    src="/images/rocket-ball.png"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover z-0"
                    style={{ pointerEvents: 'none' }}
                  />
                )}
                {/* Pokemon Image - on top of background */}
                <img
                  key={`${pokemonName}-${pokemonIdForImage || 'no-id'}`}
                  src={currentImage}
                  alt={pokemonName}
                  className="w-14 h-14 object-contain relative z-10"
                  onError={handleImageError}
                  onLoad={(e) => {
                    // Force image to show when loaded
                    e.target.style.opacity = '1';
                  }}
                  loading="eager"
                />
              </div>
              {rank === 1 && (
                <Star className="absolute -top-1 -right-1 w-5 h-5 text-yellow-500 fill-yellow-500 drop-shadow-lg z-20" />
              )}
            </div>

            {/* Pokemon Info */}
            <div className="flex-1 min-w-0">
              <h4 className={`font-bold truncate ${rank <= 3 ? 'text-gray-900 text-lg' : 'text-gray-800'}`}>
                {gamePokemonName(navId) || pokemonName}
              </h4>
              
              {/* Moves */}
              <div className="flex flex-wrap gap-1 mt-2">
                {fastMove && (
                  <span className={`
                    px-2 py-0.5 rounded-full text-xs font-medium
                    ${getMoveTypeColor(fastMove)}
                    shadow-sm
                  `}>
                    <Zap className="w-3 h-3 inline mr-1" />
                    {fastMove}
                  </span>
                )}
                {chargedMove && (
                  <span className={`
                    px-2 py-0.5 rounded-full text-xs font-medium
                    ${getMoveTypeColor(chargedMove)}
                    shadow-sm
                  `}>
                    ⚡ {chargedMove}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
            <Swords className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{t('counters.title')} {gamePokemonName(pokemonId) || pokemonName}</h2>
            <p className="text-white/80 text-sm">{t('counters.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b-2 border-gray-200 bg-white/90 backdrop-blur sticky top-0 z-10">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex-1 py-4 px-4 text-center font-medium transition-all duration-300 relative
              ${activeTab === tab.id 
                ? `bg-gradient-to-r ${tab.color} text-white shadow-lg` 
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }
            `}
          >
            <div className={`flex flex-col items-center gap-1 ${activeTab === tab.id ? '' : 'opacity-70'}`}>
              <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-white' : 'text-gray-500'}`} />
              <span className={`text-sm font-semibold ${activeTab === tab.id ? 'text-white' : 'text-gray-600'}`}>
                {tab.label}
              </span>
              <span className={`
                px-2 py-0.5 rounded-full text-xs font-bold
                ${activeTab === tab.id 
                  ? 'bg-white/30 text-white backdrop-blur-sm' 
                  : 'bg-gray-200 text-gray-600'
                }
              `}>
                {tab.data?.length || 0}
              </span>
            </div>
            {/* Active indicator */}
            {activeTab === tab.id && (
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${tab.color} rounded-t-full`} />
            )}
          </button>
        ))}
      </div>

      {/* Raid Info (only for raid tab) */}
      <AnimatePresence mode="wait">
        {activeTab === 'raid' && countersData.raidCounters[0] && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-b border-purple-100"
          >
            <div className="flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-purple-500" />
                <div>
                  <span className="text-gray-500">{t('counters.minTrainers')}</span>
                  <span className="font-bold text-purple-700 ml-1">{countersData.raidCounters[0].min_trainers}</span>
                </div>
              </div>
              <div className="h-8 w-px bg-purple-200"></div>
              <div className="flex items-center space-x-2">
                <Star className="w-5 h-5 text-yellow-500" />
                <div>
                  <span className="text-gray-500">{t('counters.recommended')}</span>
                  <span className="font-bold text-purple-700 ml-1">
                    {countersData.raidCounters[0].optimal_min}-{countersData.raidCounters[0].optimal_max}
                  </span>
                </div>
              </div>
              <div className="h-8 w-px bg-purple-200"></div>
              <div className="flex items-center space-x-2">
                <span className="text-2xl">👥</span>
                <div>
                  <span className="text-gray-500">{t('counters.easyWith')}</span>
                  <span className="font-bold text-green-600 ml-1">{countersData.raidCounters[0].easy_with}+</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div className="p-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
          >
            {activeTabData.data?.slice(0, 10).map((counter, index) => (
              <CounterCard 
                key={index} 
                counter={counter} 
                index={index}
                type={activeTab}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer tip */}
      <div className="bg-gray-50 px-6 py-3 text-center text-xs text-gray-500 border-t border-gray-100">
        {countersData.using_base_data && (
          <div className="text-amber-600 mb-1">
            {t('counters.fallbackWarning')}
          </div>
        )}
        {t('counters.tip')}
      </div>
    </div>
  );
};

export default PokemonCounters;
